<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div
        :class="[
            isInput ? 'el-input' : 'el-textarea',
            isInput ? 'qa-input' : 'qa-textarea',
            {
                [`${isInput ? 'el-input' : 'el-textarea'}--${inputSize}`]: inputSize,
                [`${isInput ? 'el-input' : 'el-textarea'}--has-label`]: label,
                'is-disabled': disabled,
                'is-focus': focused,
                'is-active': isActive,
            },
        ]"
        @mouseenter="hovering = true"
        @mouseleave="hovering = false"
        @mousedown="handleMouseDown"
        @click="handleClick"
    >
        <div v-if="label && inputSize === 'small'">
            {{ label }}
        </div>

        <div
            v-if="isInput"
            ref="container"
            :class="[
                'el-input__container',
                'qa-input-container',
                {
                    'is-embedded': embedded,
                },
            ]"
        >
            <div v-if="$slots.prefix || prefixIcon" class="el-input__prefix">
                <slot name="prefix">
                    <a-glyph
                        v-if="prefixIcon"
                        :name="prefixIcon"
                        :fill="iconColor"
                        class="el-input__icon qa-input-icon"
                        :class="{ 'el-input__icon--control': onIconClick }"
                        @click="handleIconClick"
                    />
                </slot>
            </div>

            <div class="el-input__wrapper">
                <div
                    v-if="label && inputSize !== 'small'"
                    class="el-input__label qa-input-label"
                    :class="{
                        'is-active': isActive,
                        'el-input__label--prefix': !!prefixIcon,
                    }"
                >
                    {{ label }}
                </div>
                <div
                    v-if="mask"
                    v-show="showPlaceholder"
                    :class="[
                        'qa-input-placeholder',
                        'el-input__placeholder',
                        prefixIcon ? 'el-input__placeholder--prefix' : '',
                    ]"
                >
                    {{ placeholder }}
                </div>

                <template v-if="$slots.selectPrefix">
                    <div class="el-input__select-prefix qa-input-select-prefix">
                        <slot name="selectPrefix" />
                    </div>
                </template>

                <input
                    :id="id || undefined"
                    ref="input"
                    class="qa-input-editor el-input__editor"
                    v-bind="$attrs"
                    :class="{
                        'el-input__editor--prefix': prefixIcon,
                        'is-readonly': readonly
                    }"
                    step="any"
                    :autofocus="autofocus"
                    :placeholder="mask ? null : placeholder"
                    :aria-label="label"
                    :type="inputType"
                    :autocomplete="autoComplete"
                    :disabled="disabled"
                    :readonly="readonly"
                    :value="currentValue"
                    :min="min"
                    :max="max"
                    :name="name"
                    spellcheck="false"
                    @focus="handleFocus"
                    @input="handleInput"
                    @blur="handleBlur"
                    @click="handleClickMask"
                    @change="handleChange"
                />

                <input
                    v-if="readonly"
                    ref="inputGhost"
                    class="el-input__editor el-input__editor--absolute-position"
                    :class="{
                        'el-input__editor--prefix': prefixIcon,
                        'el-input__editor--select-prefix': !!$slots.selectPrefix,
                        'el-input__editor--select-suffix': !!$slots.selectSuffix,
                    }"
                    v-bind="$attrs"
                    :type="inputType"
                    :placeholder="mask ? null : placeholder"
                    :aria-label="label"
                    :autocomplete="autoComplete"
                    :value="currentValue"
                    :disabled="disabled"
                    readonly
                    tabindex="-1"
                    spellcheck="false"
                    @focusout="handleBlur"
                />

                <template v-if="$slots.selectSuffix">
                    <div class="el-input__select-suffix qa-input-select-suffix">
                        <slot name="selectSuffix" />
                    </div>
                </template>
            </div>

            <div
                v-if="isSuffixVisible || $slots.suffix"
                class="el-input__suffix qa-input-suffix"
            >
                <a-glyph
                    v-if="showPasswordIcon"
                    :name="passwordIconName"
                    class="qa-input-icon el-input__icon el-input__icon--control"
                    :fill="iconColor"
                    tabindex="-1"
                    @click="handlePasswordIconClick"
                />
                <a-glyph
                    v-if="showClear"
                    name="times"
                    tabindex="-1"
                    :fill="iconColor"
                    class="el-input__icon el-input__icon--control el-input__icon--close"
                    @keydown.enter.stop.prevent="clear"
                    @click="clear"
                />
                <slot v-else name="suffix">
                    <a-glyph
                        v-if="suffixIcon"
                        :name="suffixIcon"
                        :fill="iconColor"
                        class="el-input__icon"
                        :class="{ 'el-input__icon--control': onIconClick }"
                        @click="handleIconClick"
                    />
                </slot>
            </div>

            <div
                v-if="required && !disabled"
                class="qa-input-required el-input__required"
            >
                <a-glyph name="i-star--16" />
            </div>
        </div>

        <div
            v-else
            class="el-textarea__container qa-textarea-container"
        >
            <div
                v-if="label && inputSize !== 'small'"
                class="el-textarea__label qa-textarea-label"
                :class="{ 'is-active': isActive }"
            >
                {{ label }}
            </div>
            <textarea
                :id="id || undefined"
                ref="textarea"
                v-bind="$attrs"
                class="el-textarea__editor qa-textarea-editor"
                :autofocus="autofocus"
                :style="textareaStyle"
                :disabled="disabled"
                :value="currentValue"
                :aria-label="label"
                :placeholder="placeholder"
                :readonly="readonly"
                :rows="rows"
                :name="name"
                spellcheck="false"
                @focus="handleFocus"
                @input="handleInput"
                @blur="handleBlur"
                @change="handleChange"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, inject, nextTick } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import { useDebouncedFn } from '@core/composables/useDebouncedFn.ts';
import calcTextareaHeight from './calcTextareaHeight.js';

// NB: import dynamically for lazy load
let IMask = null;
const IMaskImport = () => import('imask');

const emit = defineEmits([
    'update:modelValue',
    'input',
    'change',
    'focus',
    'blur',
    'click',
    'clear',
]);

const props = defineProps({
    id: { type: String, default: '' },
    type: { type: String, default: 'text' },
    prefixValue: { type: String, default: '' },
    modelValue: { type: [String, Number, Array, Boolean, Object], default: '' },
    placeholder: { type: String, default: '' },
    autoComplete: { type: String, default: 'off' },
    autofocus: Boolean,
    clearable: Boolean,
    disabled: Boolean,
    embedded: Boolean,
    readonly: Boolean,
    required: Boolean,
    name: { type: String, default: '' },
    max: { type: [String, Number], default: '' },
    min: { type: [String, Number], default: '' },
    step: { type: [String, Number], default: '' },
    mask: { type: Object, default() {} },
    label: { type: String, default: '' },
    prefixIcon: { type: String, default: '' },
    suffixIcon: { type: String, default: '' },
    onIconClick: { type: Function, default: () => {} },
    size: { type: String, default: '' },
    rows: { type: Number, default: 2 },
    autosize: { type: [Boolean, Object], default: true },
    resize: { type: String, default: '' },
    validateEvent: { type: Boolean, default: true },
    showPasswordIcon: Boolean,
    defaultType: { type: String, default: 'password' },
    precision: { type: Number, default: 10 },
    debounceProcessMask: { type: Number, default: 0 },
    debounceProcessNumber: { type: Number, default: 500 },
    iconColor: { type: String, default: 'brand-primary' },
    freezeFocus: { type: Boolean, default: false },
});

const input = ref(null);
const textarea = ref(null);
const inputGhost = ref(null);
const container = ref(null);
const focused = ref(false);
const hovering = ref(false);
const currentValue = ref('');
const userNumberInput = ref('');
const isPasswordHidden = ref(props.defaultType === 'password');
const textareaCalcStyle = ref({});
const registerInput = inject('elFormItemRegisterInput', () => {});


const elFormItem = inject('elFormItem', null);
const inputSize = computed(() => props.size);
const isInput = computed(() => props.type !== 'textarea');
const isTextarea = computed(() => props.type === 'textarea');
const isNumber = computed(() => props.type === 'number');
const isSuffixVisible = computed(() => Boolean(props.suffixIcon) || props.showPasswordIcon || props.clearable);
const showPlaceholder = computed(() => props.prefixValue === currentValue.value || !currentValue.value);
const passwordIconName = computed(() => isPasswordHidden.value ? 'closed-eye' : 'opened-eye');
const inputType = computed(() => {
    if (props.type === 'password' && props.showPasswordIcon) {
        return isPasswordHidden.value ? 'password' : 'text';
    }
    return props.type;
});

const isEmptyValue = computed(() =>
    currentValue.value === '' ||
    currentValue.value === null ||
    currentValue.value === undefined ||
    (Array.isArray(currentValue.value) && currentValue.value.length === 0),
);
const isActive = computed(() => !isEmptyValue.value || props.placeholder || focused.value);
const showClear = computed(() => props.clearable && currentValue.value !== '');
const textareaStyle = computed(() => ({
    ...textareaCalcStyle.value,
    resize: props.resize,
}));

const { debouncedFn: triggerProcessChangeValue } = useDebouncedFn((val) => {
    processChangeValue(val);
}, props.debounceProcessNumber);

const { debouncedFn: triggerProcessMaskChangeValue } = useDebouncedFn(() => {
    processMaskChangeValue();
}, props.debounceProcessMask);


const setCurrentValue = (value) => {
    if (value === currentValue.value) return;
    nextTick(() => {
        resizeTextarea();
    });

    currentValue.value = value;
    emit('update:modelValue', value);
    emit('change', value);
};

watch(() => props.modelValue, (val) => {
    let newVal = val;
    if (isNumber.value) {
        newVal = formatNumber(newVal);
        emit('input', newVal);
        emit('change', newVal);
    }
    setCurrentValue(newVal);

    if (props.validateEvent) {
        elFormItem?.validate?.('change');
        elFormItem?.markAsTouched?.();
    }
});


onMounted(async () => {
    if (props.mask && props.type === 'text') {
        IMask = await IMaskImport();
    }

    resizeTextarea();
    registerInput({ focus });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    props.autofocus && focus();

    if (props.modelValue !== undefined) {
        let newVal = props.modelValue;
        if (isNumber.value) {
            newVal = formatNumber(newVal);
        }
        setCurrentValue(newVal);
    }

    if (props.mask && props.type === 'text') {
        triggerProcessMaskChangeValue();
    }
    if (props.prefixValue.length > props.modelValue.length) {
        setCurrentValue(props.prefixValue);
    }
});

const handleInput = (event) => {
    if (props.debounceProcessMask && props.mask && props.type === 'text') {
        emit('input', event.target.value);
        emit('change', event.target.value);
        triggerProcessMaskChangeValue();
    } else {
        processInputValue(event);
    }
};

const handleChange = (event) => {
    processChangeValue(event.target.value);
    if (props.validateEvent) {
        elFormItem?.validate?.('change');
        elFormItem?.markAsTouched?.();
    }
};

const handleFocus = (e) => {
    focused.value = true;
    emit('focus', e);
    elFormItem?.markAsFocused?.();
    elFormItem?.markAsTouched?.();
};

const handleBlur = (e) => {
    if (!props.freezeFocus) focused.value = false;
    emit('blur', e);
    if (props.validateEvent) {
        elFormItem?.validate?.('blur');
        emit('change', currentValue.value);
    }
    elFormItem?.markAsBlurred?.();
};

const handleMouseDown = (e) => {
    if (props.disabled) return;
    if (e.target !== input.value && e.target !== textarea.value) {
        e.preventDefault();
    }
    focus();
};

const handleClick = (e) => {
    if (props.disabled) return;
    emit('click', e);
};

const handleIconClick = (e) => {
    if (!props.disabled && props.onIconClick) {
        props.onIconClick(e);
    }
};

const handleClickMask = (e) => {
    if (e.target.selectionStart < props.prefixValue.length) {
        nextTick(() => {
            e.target.setSelectionRange(props.prefixValue.length, props.prefixValue.length);
        });
    }
};

const processInputValue = (event) => {
    let value = event.target.value;

    if (props.mask && props.type === 'text') {
        const masked = new IMask(input.value, props.mask);
        value = masked.value;
        masked.destroy();
    }

    if (props.prefixValue.length > value.length) {
        setCurrentValue(props.prefixValue);
        emit('input', props.prefixValue);
        emit('change', props.prefixValue);
        // eslint-disable-next-line no-param-reassign
        event.target.value = props.prefixValue;
    } else {
        if (isNumber.value) {
            userNumberInput.value = value;
            triggerProcessChangeValue(userNumberInput.value);
        } else if (props.prefixValue && !props.mask && !value.startsWith(props.prefixValue)) {
            value = props.prefixValue + value;
            setCurrentValue(value);
        } else {
            setCurrentValue(value);
        }
        emit('input', value);
        emit('change', value);
    }
};

const processChangeValue = (val) => {
    let value = val;

    if (props.type === 'number') {
        value = formatNumber(userNumberInput.value || val);
        userNumberInput.value = null;
    }
    if (value !== currentValue.value) {
        setCurrentValue(value);
    }
    emit('change', value);
};

const hasValidNumber = (val) => val !== '' && val != null && !isNaN(Number(val));
const toPrecision = (val) => Number.parseFloat(Math.round(val * Math.pow(10, props.precision)) / Math.pow(10, props.precision));
const formatNumber = (value) => {
    if (value === '') return '';
    let newVal = Number(value);
    if (isNaN(newVal)) return '';
    if (typeof props.precision === 'number' && Number.isFinite(props.precision)) {
        newVal = toPrecision(newVal);
    }
    if (hasValidNumber(props.max) && newVal > Number(props.max)) return Number(props.max);
    if (hasValidNumber(props.min) && newVal < Number(props.min)) return Number(props.min);
    return newVal;
};


const processMaskChangeValue = () => {
    const masked = new IMask(input.value, props.mask);
    const value = masked.value;
    masked.destroy();
    setCurrentValue(value);

    emit('input', value);
    emit('change', value);
};

const handlePasswordIconClick = () => {
    const { selectionStart, selectionEnd } = input.value;
    isPasswordHidden.value = !isPasswordHidden.value;
    nextTick(() => {
        input.value.focus();
        input.value.setSelectionRange(selectionStart, selectionEnd);
    });
    handleIconClick();
};

const resizeTextarea = () => {
    if (!isTextarea.value) return;
    if (!props.autosize) {
        textareaCalcStyle.value = {
            minHeight: calcTextareaHeight(textarea.value).minHeight,
        };
        return;
    }
    textareaCalcStyle.value = calcTextareaHeight(textarea.value, props.autosize.minRows, props.autosize.maxRows);
};

const focus = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    input.value?.focus() || textarea.value?.focus();
};

const clear = () => {
    emit('change', '');
    emit('input', '');
    emit('clear');
    setCurrentValue('');
    focus();
};

defineExpose({
    focus,
});
</script>

<style lang="postcss" scoped>
input, textarea {
    &::-webkit-contacts-auto-fill-button {
        visibility: hidden;
        display: none !important;
        pointer-events: none;
        position: absolute;
        right: 0;
    }
}
.el-input {
    display: block;
    width: 100%;
    &.is-focus {
        .el-input__container,
        .el-textarea__container {
            border-color: var(--av-brand-primary);
        }
    }
    input::placeholder {
        color: var(--av-fixed-light);
        opacity: 1;
    }
    &.is-error {
        .el-input__container,
        .el-textarea__container {
            border-color: var(--av-fixed-danger);
        }
    }
    &__container {
        box-sizing: border-box;
        display: flex;
        align-items: center;
        height: 48px;
        border: 1px solid var(--av-brand-light);
        border-radius: 4px;
        padding: 0 8px;
        background-color: var(--av-inversed-primary);
        background-clip: padding-box;
        cursor: text;

    }
    &__wrapper {
        order: 1;
        box-sizing: border-box;
        display: flex;
        flex: 1 1 auto;
        height: 100%;
        padding: 0 8px;
        position: relative;
        &:has(.el-input__label) {
            padding-top: 11px;
        }
    }
    &__label {
        @mixin body;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--av-fixed-light);
        padding: 11px 8px 0;
        border-radius: inherit;
        background-color: inherit;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        &.is-active {
            @mixin caption-accent;
            padding-top: 4px;
        }
    }
    &__editor {
        @mixin body;
        width: 100%;
        &.is-readonly {
            position: absolute;
            opacity: 0;
        }
        &::placeholder {
            color: var(--av-fixed-light);
        }
    }
    &__icon {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        &.a-glyph_size_s {
            width: 16px;
            height: 16px;
        }
    }
    &__prefix {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    &__suffix {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 16px;
        width: 16px;
        min-width: 32px;
        order: 3;
    }
}
.el-textarea {
    &__container {
        position: relative;
        border: 1px solid var(--av-brand-light);
        border-radius: 4px;
        background-color: var(--av-inversed-primary);
        background-clip: padding-box;
        box-sizing: border-box;
    }
    &__label {
        @mixin body;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--av-fixed-light);
        border-radius: inherit;
        background-color: inherit;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding: 11px 16px 0;
        &.is-active {
            @mixin caption-accent;
            padding-top: 4px;
        }
    }
    &__editor {
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
        color: var(--av-fixed-primary);
        display: block;
        width: 100%;
        border: none;
        resize: vertical;
        border-radius: 4px;
        background-color: transparent;
        position: relative;
        min-height: 51px;
        height: 51px;
        appearance: none;
        padding: 0 16px 3px;
        font-family: var(--font-family-default);

        &::placeholder {
            @mixin body;
            font-weight: 400;
            color: var(--av-fixed-light);
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            cursor: text;
            user-select: none
        }
    }
    textarea:focus {
        outline: none;
    }
    &--has-label {
        .el-textarea__container {
            padding-top: 17px;
        }
    }
}
</style>

<style lang="postcss">
.el-form-item {
    &.is-error {
        .el-input__container,
        .el-textarea__container,
        .el-file-picker__inner {
            border-color: var(--av-fixed-danger);
        }
    }
    &.is-focus {
        .el-input__container,
        .el-textarea__container,
        .el-file-picker__inner {
            border-color: var(--av-brand-primary);
        }
        .form-error {
            color: var(--av-fixed-light);
        }
    }
}
</style>
